import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import { BaseMcpServer } from './BaseMcpServer.js';
import express, { type Express, type Request, type Response } from 'express';
import http from 'http';
import type { AddressInfo } from 'node:net';
import { randomUUID } from 'node:crypto';

/**
 * 这是我们具体的 HTTP 服务器实现。
 * 它继承了 BaseMcpServer 的所有基础功能，并添加了启动和停止 Web 服务器的逻辑。
 * 它就像给毛坯房装上了门窗和水电，让它能真正地对外服务。
 */
export class SoraVideoHttpServer extends BaseMcpServer {
  private app: Express;
  private server?: http.Server; // '?' 表示这个属性可能是 undefined
  private port: number;
  private host: string;
  private transport?: StreamableHTTPServerTransport;

  constructor(port: number = 8080, host: string = '0.0.0.0') {
    // super() 必须首先被调用，用来初始化父类 (BaseMcpServer)
    super();
    this.port = port;
    this.host = host;
    this.app = express();

    // 添加 JSON 解析中间件
    this.app.use(express.json());

    this.registerTransportRoutes();
  }

  // 实现父类中定义的抽象方法 start
  public async start(): Promise<void> {
    if (this.state === 'running') {
      console.warn('[SoraHttpServer] Server is already running.');
      return;
    }
    console.log(`[SoraHttpServer] Starting Sora Video MCP Server (HTTP Mode) on ${this.host}:${this.port}...`);
    this.state = 'running';

    // 创建一个 MCP 的 HTTP 传输层实例
    this.transport = new StreamableHTTPServerTransport({
      sessionIdGenerator: () => randomUUID(),
      enableJsonResponse: true,
    });

    // 将我们的 Express 应用与 MCP 传输层连接起来
    // 这会让 SDK 在 /mcp 端点上自动处理所有符合协议的 GET、POST 与 DELETE 请求

    // 将我们的服务器核心逻辑与传输层连接起来
    await this.sdkServer.connect(this.transport);

    // 启动 Express 的 HTTP 服务器，开始监听指定端口
    // 我们将 server 实例保存下来，以便后续可以关闭它
    await new Promise<void>(resolve => {
      this.server = this.app.listen(this.port, this.host, () => {
        const address = this.server?.address() as AddressInfo | string | null | undefined;
        if (address && typeof address === 'object') {
          this.port = address.port;
          this.host = address.address;
        }

        const displayHost = this.host === '0.0.0.0' ? 'localhost' : this.host;
        console.log(`[SoraHttpServer] Sora Video MCP Server is running on http://${displayHost}:${this.port}`);
        console.log(`[SoraHttpServer] MCP endpoint is available at http://${displayHost}:${this.port}/mcp`);
        resolve();
      });
    });
  }

  // 实现父类中定义的抽象方法 stop
  public async stop(): Promise<void> {
    if (this.state !== 'running' || !this.server) {
      console.warn('[SoraHttpServer] Server is not running or not initialized.');
      return;
    }
    console.log('[SoraHttpServer] Stopping server...');
    this.state = 'stopping';

    // 使用 Promise 来优雅地关闭服务器
    await new Promise<void>((resolve, reject) => {
      this.server?.close((err) => {
        if (err) {
          console.error('[SoraHttpServer] Error closing server:', err);
          reject(err);
          return;
        }
        console.log('[SoraHttpServer] Server has been stopped.');
        this.state = 'idle';
        resolve();
      });
    });

    await this.transport?.close();
    this.transport = undefined;
  }

  private registerTransportRoutes(): void {
    const routeHandler = async (req: Request, res: Response): Promise<void> => {
      if (!this.transport) {
        res.status(503).json({ error: 'MCP transport not initialized' });
        return;
      }

      try {
        const parsedBody = (req as Request & { body?: unknown }).body;
        await this.transport.handleRequest(req, res, parsedBody);
      } catch (error) {
        console.error('[SoraHttpServer] Failed to handle MCP request:', error);
        if (!res.headersSent) {
          res.status(500).json({ error: 'Internal Server Error' });
        }
      }
    };

    this.app.get('/mcp', routeHandler);
    this.app.post('/mcp', routeHandler);
    this.app.delete('/mcp', routeHandler);
  }
}
